import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandLoader } from '@/shared/ui/BrandLoader';

import { ThemeToggle } from './ThemeToggle';

/* The frame around signing in: brand, one line about what the product is for, the form, and one
 * line about where the session lives.
 *
 * The design spec's P0 names this as undesigned, and it was — the two auth routes were a
 * `maxWidth: 420px` inline style around a bare `Card`, rendered on the page surface with no brand
 * and nothing to say what the product was. This is the mockup's arrangement: centred, 380px, brand
 * above the card, tagline, elevated card, footer note.
 *
 * **The footer line is a fact rather than reassurance copy.** `session.ts` stores the token in
 * `sessionStorage` and says why: "a token that outlives the tab is a token somebody else can find".
 * Saying so on the login screen is the only place a user learns that closing the tab signs them
 * out — otherwise they discover it by being signed out and reading it as a bug.
 *
 * **The theme toggle is here deliberately.** It lives in the app's top bar, which does not exist
 * until somebody is signed in, so without this the two screens a user meets first are the two they
 * cannot set the theme on. It is also the only control on the page that works before authentication,
 * which is a reasonable thing for a login screen to offer.
 *
 * **In `app/` rather than `shared/ui/` because it holds `ThemeToggle`**, which is an app-layer
 * component: the FSD layering runs app -> features -> shared, so a shared component importing one
 * from `app` is an upward import and ESLint's `boundaries` rule fails the build on it. `AppShell`
 * lives here for the same reason, and these two are the same kind of thing - the frame the router
 * renders a route inside.
 *
 * ---
 *
 * **`__main` exists to keep the toggle out of the centred column, and it fixes a real overlap.**
 * The toggle used to be `position: absolute` in the corner while the four content blocks were
 * centred by `justify-content: center`. Centring a flex column whose content is *taller* than the
 * viewport starts that content at the top edge rather than above it — so on the signup form at
 * 420x620 the wordmark rendered at y=32 with the toggle at y=16 beside it, all but touching, and
 * narrower still would have overlapped outright. A grid with the toggle in its own row cannot
 * produce that state at any size, which is why this is a structural fix rather than a padding one.
 *
 * The footer stays *inside* `__main` and is deliberately not pinned to the bottom of the viewport:
 * it is a sentence about the session the card is about to open, so it belongs with the card rather
 * than at the far edge of a tall screen.
 *
 * **The four children arrive in sequence, and this is `--motion-stagger`'s first consumer.** That
 * token and `--stagger-max` were added with the motion system and used by nothing — the shape this
 * codebase records as "green does not mean wired", in the stylesheet. A cascade is wrong on a
 * working surface (a table whose rows assemble themselves is a loading state nobody asked for) and
 * right here: this is a destination rather than a surface, it is the first screen in the product,
 * and there are four blocks rather than two hundred. The order is the reading order — mark, what
 * this is, the form, the footnote — so the sequence carries the same information the layout does.
 * Under `prefers-reduced-motion` the step is zeroed and all four land together.
 */

export interface AuthShellProps {
  readonly children: ReactNode;
}

export function AuthShell({ children }: AuthShellProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="lq-auth">
      {/* Row one, in flow. The stagger index is last so the utility settles after the content it
          sits above — a theme switch is not what somebody came here to read. */}
      <div className="lq-auth__corner">
        <ThemeToggle />
      </div>

      <div className="lq-auth__main">
        {/* The logo, drawing itself once and holding on the finished lockup — the brand's own
            animation, so the first screen ends on the mark rather than looping beside a password
            field. It carries the product's name for a screen reader. Inside the brand block, so the
            entrance cascade keeps its steps. */}
        <div className="lq-auth__brand">
          <BrandLoader once label={t('brand.logoLabel')} />
        </div>
        {/* **What the product is, not what to do next.** This slot read "Verify your email." on
            every auth route, including the one a first-time visitor lands on — so the sentence
            whose job is to say what Lumiq is instructed them to act on a message nobody had sent
            them. A route-agnostic slot holding route-specific copy; the forms each say their own
            next step, and this says what they are a way into. */}
        <p className="lq-auth__tagline">{t('auth.tagline')}</p>

        <div className="lq-auth__card">{children}</div>

        <p className="lq-auth__foot">{t('auth.deployment')}</p>
      </div>
    </div>
  );
}
